type CleanupStack = Array<() => void>;

type DispatchOptions<T> = {
    el: HTMLElement;
    name: string;
    detail?: T;
    composed?: boolean;
};

type AddListenerOptions = {
    target: EventTarget;
    event: string;
    handler: EventListener;
    cleanup?: CleanupStack;
    options?: globalThis.AddEventListenerOptions;
};

type PresenceOptions = {
    el: HTMLElement;
    attr: string;
    present: boolean;
    value?: string;
};

type AttributeOptions = {
    el: HTMLElement;
    attr: string;
    value: string;
};

type ElementOptions = {
    el: HTMLElement;
};

type BoolOptions = {
    value: string | undefined;
};

const TRUE_STRINGS = ['true', '1', 'yes', 'on'];

const asBool = ({value}: BoolOptions): boolean => (
    value !== undefined && (value === '' || TRUE_STRINGS.includes(value.toLowerCase()))
);

const dispatch = <T = unknown>({el, name, detail, composed = false}: DispatchOptions<T>): void => {
    const eventInit: CustomEventInit<T> = {bubbles: true, composed};
    if (detail !== undefined) eventInit.detail = detail;
    el.dispatchEvent(new CustomEvent(name, eventInit));
};

const addListener = ({
    target,
    event,
    handler,
    cleanup,
    options,
}: AddListenerOptions): (() => void) => {
    target.addEventListener(event, handler, options);
    const remove = (): void => target.removeEventListener(event, handler, options);
    cleanup?.push(remove);
    return remove;
};

const setPresence = ({el, attr, present, value = ''}: PresenceOptions): void => {
    if (present) {
        if (el.getAttribute(attr) !== value) el.setAttribute(attr, value);
        return;
    }

    el.removeAttribute(attr);
};

const setAttribute = ({el, attr, value}: AttributeOptions): void => {
    if (el.getAttribute(attr) !== value) el.setAttribute(attr, value);
};

const removeAttribute = ({el, attr}: Omit<AttributeOptions, 'value'>): void => {
    if (el.hasAttribute(attr)) el.removeAttribute(attr);
};

const isRtl = ({el}: ElementOptions): boolean => {
    const closestDir = el.closest<HTMLElement>('[dir]');
    return (closestDir?.dir || document.dir).toLowerCase() === 'rtl';
};

export {addListener, asBool, dispatch, isRtl, removeAttribute, setAttribute, setPresence};
export type {AddListenerOptions, AttributeOptions, BoolOptions, CleanupStack, DispatchOptions, ElementOptions, PresenceOptions};
